/**
 * AgentRegistryTab — Agent registry management, profiles, roadmap, contacts, and work items.
 */

import React, { useState, useEffect } from 'react';

import {
  RefreshCw, Plus, CheckCircle, XCircle, AlertTriangle, Bot,
  Loader2, ChevronRight, Calendar, Rocket, Code, History,
  MessageSquare, Mail, Navigation, Star, Tv, FlaskConical,
  ClipboardList, CircleDot, Save, X, Edit3, Trash2,
  Database, HardDrive, Globe, FileText, Link2, Clock,
  Zap, Archive, ExternalLink, Radio, Lock
} from 'lucide-react';
import type { RegistryAgent, RegistryData, AgentSkill } from './types';
import { API_BASE, authFetch } from './constants';

interface AgentRegistryTabProps {
  registryData: RegistryData | null;
  loading: boolean;
  onRefresh: () => void;
}

const STATUS_CONFIG: Record<string, { label: string; cssColor: string; icon: React.ReactNode; bgVar: string }> = {
  active: { label: 'Active', cssColor: 'var(--idx-text-primary)', icon: <CheckCircle size={14} />, bgVar: 'var(--idx-bg-success)' },
  in_development: { label: 'In Development', cssColor: 'var(--idx-text-primary)', icon: <Code size={14} />, bgVar: 'var(--idx-bg-warn)' },
  uat: { label: 'UAT', cssColor: 'var(--idx-text-primary)', icon: <FlaskConical size={14} />, bgVar: 'var(--idx-bg-success)' },
  planned: { label: 'Planned', cssColor: 'var(--idx-text-primary)', icon: <Calendar size={14} />, bgVar: 'var(--idx-bg-success)' },
  deprecated: { label: 'Deprecated', cssColor: 'var(--idx-text-primary)', icon: <XCircle size={14} />, bgVar: 'var(--idx-bg-error)' },
  prod: { label: 'Production', cssColor: 'var(--idx-text-primary)', icon: <CheckCircle size={14} />, bgVar: 'var(--idx-bg-success)' },
};

// Safe lookup — any status not in STATUS_CONFIG (e.g. a future/legacy value)
// falls back to the "active" treatment instead of crashing the registry.
const getStatusConfig = (status?: string) => STATUS_CONFIG[status || 'active'] || STATUS_CONFIG.active;

const AgentRegistryTab: React.FC<AgentRegistryTabProps> = ({ registryData, loading, onRefresh }) => {
  const [editingAgent, setEditingAgent] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<RegistryAgent>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [showAddAgent, setShowAddAgent] = useState(false);
  const [newAgentForm, setNewAgentForm] = useState<Partial<RegistryAgent>>({
    status: 'planned',
    sampleQuestions: [],
    disabled: false,
    hidden: false,
  });

  // Fetch on first render
  useEffect(() => {
    if (!registryData && !loading) {
      onRefresh();
    }
  }, [registryData, loading, onRefresh]);

  const startEditing = (agent: RegistryAgent) => {
    setEditingAgent(agent.id);
    setEditForm({
      name: agent.name,
      description: agent.description,
      status: agent.status || 'active',
      plannedDate: agent.plannedDate || '',
      roadmapNotes: agent.roadmapNotes || '',
      sampleQuestions: [...(agent.sampleQuestions || [])],
      disabled: agent.disabled,
      hidden: agent.hidden,
      version: agent.version || '',
      releaseDate: agent.releaseDate || '',
      demoDate: agent.demoDate || '',
      uatStartDate: agent.uatStartDate || '',
      uatEndDate: agent.uatEndDate || '',
      routingTips: agent.routingTips || '',
      introMessage: agent.introMessage || '',
      featured: agent.featured || false,
      contacts: agent.contacts ? [...agent.contacts] : [],
      workItems: agent.workItems ? agent.workItems.map(wi => ({ ...wi })) : [],
      versionHistory: agent.versionHistory ? agent.versionHistory.map(vh => ({ ...vh })) : [],
      updateNotice: agent.updateNotice ? { ...agent.updateNotice } : { enabled: false, date: '', summary: '' },
      accessChannels: agent.accessChannels ? agent.accessChannels.map(ch => ({ ...ch })) : [],
      accessControl: agent.accessControl ? { ...agent.accessControl } : undefined,
      skills: agent.skills ? agent.skills.map(s => ({ ...s, tags: [...(s.tags || [])], examples: [...(s.examples || [])] })) : [],
    });
    setSaveError(null);
    setSaveSuccess(null);
  };

  const cancelEditing = () => {
    setEditingAgent(null);
    setEditForm({});
    setSaveError(null);
  };

  const saveAgent = async (agentId: string) => {
    setSaving(true);
    setSaveError(null);
    try {
      const response = await authFetch(`${API_BASE}/admin/registry/${agentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editForm),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || `Save failed: ${response.status}`);
      }

      setSaveSuccess(`${editForm.name || agentId} updated successfully`);
      setEditingAgent(null);
      setEditForm({});
      onRefresh();
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const createAgent = async () => {
    if (!newAgentForm.id || !newAgentForm.name) {
      setSaveError('Agent ID and Name are required');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const response = await authFetch(`${API_BASE}/admin/registry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newAgentForm),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || `Create failed: ${response.status}`);
      }

      setSaveSuccess(`${newAgentForm.name} added to registry`);
      setShowAddAgent(false);
      setNewAgentForm({ status: 'planned', sampleQuestions: [], disabled: false, hidden: false });
      onRefresh();
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (err: any) {
      setSaveError(err.message || 'Failed to create agent');
    } finally {
      setSaving(false);
    }
  };

  const deleteAgent = async (agentId: string) => {
    if (!window.confirm(`Are you sure you want to remove "${agentId}" from the registry?`)) return;
    try {
      const response = await authFetch(`${API_BASE}/admin/registry/${agentId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Delete failed');
      }
      setSaveSuccess(`${agentId} removed from registry`);
      onRefresh();
      setTimeout(() => setSaveSuccess(null), 3000);
    } catch (err: any) {
      setSaveError(err.message || 'Failed to delete');
    }
  };

  const addSampleQuestion = (form: Partial<RegistryAgent>, setForm: (f: Partial<RegistryAgent>) => void) => {
    const questions = [...(form.sampleQuestions || []), ''];
    setForm({ ...form, sampleQuestions: questions });
  };

  const updateSampleQuestion = (
    form: Partial<RegistryAgent>, 
    setForm: (f: Partial<RegistryAgent>) => void, 
    index: number, 
    value: string
  ) => {
    const questions = [...(form.sampleQuestions || [])];
    questions[index] = value;
    setForm({ ...form, sampleQuestions: questions });
  };

  const removeSampleQuestion = (
    form: Partial<RegistryAgent>, 
    setForm: (f: Partial<RegistryAgent>) => void, 
    index: number
  ) => {
    const questions = [...(form.sampleQuestions || [])];
    questions.splice(index, 1);
    setForm({ ...form, sampleQuestions: questions });
  };

  if (loading) {
    return (
      <div className="idx-loading">
        <Loader2 className="idx-spinner" size={32} />
        <p>Loading agent management...</p>
      </div>
    );
  }

  if (!registryData) {
    return (
      <div className="idx-empty-state">
        <Bot size={48} />
        <h3>Agent Registry (Routing)</h3>
        <p>Load the agent registry to manage agent profiles, descriptions, sample questions, and roadmap status.</p>
        <button className="idx-refresh-btn" onClick={onRefresh}>
          <RefreshCw size={16} />
          Load Registry
        </button>
      </div>
    );
  }

  const { agents, summary, roadmap } = registryData;

  // Sort agents: active first, then in_development, then planned, then deprecated
  const statusOrder = ['active', 'in_development', 'uat', 'planned', 'deprecated'];
  const sortedAgents = [...agents].sort((a, b) => {
    const aIdx = statusOrder.indexOf(a.status || 'active');
    const bIdx = statusOrder.indexOf(b.status || 'active');
    return aIdx - bIdx;
  });

  return (
    <div className="idx-registry-tab">
      {/* Status Alerts */}
      {saveSuccess && (
        <div className="idx-registry-success">
          <CheckCircle size={16} />
          {saveSuccess}
        </div>
      )}
      {saveError && (
        <div className="idx-registry-error">
          <AlertTriangle size={16} />
          {saveError}
          <button onClick={() => setSaveError(null)}><X size={14} /></button>
        </div>
      )}

      {/* Summary Stats */}
      <div className="idx-registry-stats">
        {Object.entries(STATUS_CONFIG).map(([key, config]) => {
          const count = key === 'active' ? summary.active 
            : key === 'in_development' ? summary.inDevelopment 
            : key === 'uat' ? (summary.uat || 0)
            : key === 'planned' ? summary.planned 
            : summary.deprecated;
          return (
            <div key={key} className="idx-stat-card" style={{ borderLeft: '3px solid var(--idx-border-strong)' }}>
              <div style={{ color: config.cssColor }}>{config.icon}</div>
              <div className="idx-stat-info">
                <span className="idx-stat-value">{count}</span>
                <span className="idx-stat-label">{config.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Action buttons below stat cards */}
      <div className="idx-registry-actions">
        <button className="idx-refresh-btn" onClick={() => setShowAddAgent(true)}>
          <Plus size={16} />
          Add Agent
        </button>
        <button className="idx-refresh-btn" onClick={onRefresh}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Roadmap Timeline */}
      {roadmap.length > 0 && (
        <div className="idx-registry-roadmap">
          <h3><Rocket size={18} /> Roadmap & Timeline</h3>
          <div className="idx-roadmap-timeline">
            {roadmap.map((item, idx) => (
              <div key={idx} className="idx-roadmap-item">
                <div className="idx-roadmap-dot" style={{ 
                  backgroundColor: 'var(--idx-text-muted)'
                }} />
                <div className="idx-roadmap-content">
                  <div className="idx-roadmap-header">
                    <strong>{item.name}</strong>
                    <span className="idx-roadmap-status" style={{ 
                      color: item.status ? STATUS_CONFIG[item.status]?.cssColor : undefined,
                      backgroundColor: item.status ? STATUS_CONFIG[item.status]?.bgVar : undefined,
                      border: '1px solid var(--idx-border)',
                    }}>
                      {item.status && STATUS_CONFIG[item.status]?.icon}
                      {item.status && STATUS_CONFIG[item.status]?.label}
                    </span>
                  </div>
                  <div className="idx-roadmap-dates">
                    {item.plannedDate && (
                      <span className="idx-roadmap-date">
                        <Calendar size={12} /> Target: {item.plannedDate}
                      </span>
                    )}
                    {item.demoDate && (
                      <span className="idx-roadmap-date idx-date-demo">
                        <Tv size={12} /> Demo: {item.demoDate}
                      </span>
                    )}
                    {(item.uatStartDate || item.uatEndDate) && (
                      <span className="idx-roadmap-date idx-date-uat">
                        <FlaskConical size={12} /> UAT: {item.uatStartDate || '?'}{item.uatEndDate ? ` – ${item.uatEndDate}` : ''}
                      </span>
                    )}
                    {item.releaseDate && (
                      <span className="idx-roadmap-date idx-date-release">
                        <Rocket size={12} /> Release: {item.releaseDate}
                      </span>
                    )}
                  </div>
                  {item.roadmapNotes && <p className="idx-roadmap-notes">{item.roadmapNotes}</p>}
                  {/* Inline work items for this roadmap agent */}
                  {item.workItems && item.workItems.length > 0 && (
                    <div className="idx-roadmap-work-items">
                      {item.workItems
                        .slice()
                        .sort((a, b) => {
                          if (a.date && b.date) return a.date.localeCompare(b.date);
                          if (a.date) return -1;
                          if (b.date) return 1;
                          return 0;
                        })
                        .map((wi, wiIdx) => (
                        <div key={wiIdx} className="idx-roadmap-work-item" style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          fontSize: '0.8rem', padding: '4px 0',
                          color: wi.status === 'done' ? 'var(--idx-text-faint)' : 'var(--idx-text-secondary)',
                          textDecoration: wi.status === 'done' ? 'line-through' : 'none'
                        }}>
                          <CircleDot size={10} style={{
                            color: 'var(--idx-text-muted)',
                            flexShrink: 0
                          }} />
                          <span style={{ flex: 1 }}>{wi.title}</span>
                          {wi.date && <span style={{ color: 'var(--idx-text-muted)', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{wi.date}</span>}
                          {wi.notes && <span style={{ color: 'var(--idx-text-faint)', fontStyle: 'italic', fontSize: '0.75rem' }}>{wi.notes}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Consolidated Work Items Timeline (from all agents, sorted by date) */}
      {(() => {
        const allWorkItems = agents
          .flatMap(a => (a.workItems || []).map(wi => ({ ...wi, agentId: a.id, agentName: a.name, agentStatus: a.status })))
          .filter(wi => wi.status !== 'done')
          .sort((a, b) => {
            if (a.date && b.date) return a.date.localeCompare(b.date);
            if (a.date) return -1;
            if (b.date) return 1;
            return 0;
          });
        if (allWorkItems.length === 0) return null;
        return (
          <div className="idx-registry-roadmap" style={{ marginTop: '16px' }}>
            <h3><ClipboardList size={18} /> Work Items Timeline</h3>
            <div className="idx-roadmap-timeline">
              {allWorkItems.map((wi, idx) => (
                <div key={idx} className="idx-roadmap-item">
                  <div className="idx-roadmap-dot" style={{
                    backgroundColor: wi.status === 'in_progress' ? '#d97706' : 'var(--idx-text-muted)'
                  }} />
                  <div className="idx-roadmap-content">
                    <div className="idx-roadmap-header">
                      <strong>{wi.title}</strong>
                      <span className="idx-roadmap-status" style={{
                        color: 'var(--idx-text-primary)',
                        backgroundColor: wi.status === 'in_progress' ? 'var(--idx-bg-warn)' : 'var(--idx-bg-tertiary)',
                        border: '1px solid var(--idx-border)',
                      }}>
                        <CircleDot size={12} />
                        {wi.status === 'in_progress' ? 'In Progress' : 'Pending'}
                      </span>
                    </div>
                    <div className="idx-roadmap-dates">
                      <span className="idx-roadmap-date" style={{ color: 'var(--idx-text-muted)' }}>
                        <Bot size={12} /> {wi.agentName}
                      </span>
                      <span className="idx-roadmap-date" style={!wi.date ? { color: 'var(--idx-text-faint)', fontStyle: 'italic' } : undefined}>
                        <Calendar size={12} /> {wi.date || 'No date set'}
                      </span>
                    </div>
                    {wi.notes && <p className="idx-roadmap-notes">{wi.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Add Agent Form */}
      {showAddAgent && (
        <div className="idx-registry-add-form">
          <div className="idx-registry-form-header">
            <h3><Plus size={18} /> Add New Agent to Registry</h3>
            <button className="idx-close-btn" onClick={() => setShowAddAgent(false)}><X size={16} /></button>
          </div>
          <div className="idx-registry-form-grid">
            <div className="idx-form-group">
              <label>Agent ID *</label>
              <input 
                type="text" 
                placeholder="e.g. archimedes"
                value={newAgentForm.id || ''}
                onChange={e => setNewAgentForm({ ...newAgentForm, id: e.target.value })}
              />
            </div>
            <div className="idx-form-group">
              <label>Name *</label>
              <input 
                type="text" 
                placeholder="e.g. Archimedes"
                value={newAgentForm.name || ''}
                onChange={e => setNewAgentForm({ ...newAgentForm, name: e.target.value })}
              />
            </div>
            <div className="idx-form-group">
              <label>Status</label>
              <select 
                value={newAgentForm.status || 'planned'}
                onChange={e => setNewAgentForm({ ...newAgentForm, status: e.target.value as RegistryAgent['status'] })}
              >
                <option value="planned">Planned</option>
                <option value="in_development">In Development</option>
                <option value="uat">UAT</option>
                <option value="active">Active</option>
              </select>
            </div>
            <div className="idx-form-group">
              <label>Planned Date</label>
              <input 
                type="text" 
                placeholder="e.g. Q2 2026"
                value={newAgentForm.plannedDate || ''}
                onChange={e => setNewAgentForm({ ...newAgentForm, plannedDate: e.target.value })}
              />
            </div>
            <div className="idx-form-group">
              <label>Release Date</label>
              <input 
                type="date" 
                value={newAgentForm.releaseDate || ''}
                onChange={e => setNewAgentForm({ ...newAgentForm, releaseDate: e.target.value })}
              />
            </div>
            <div className="idx-form-group">
              <label>Demo Scheduled</label>
              <input 
                type="date" 
                value={newAgentForm.demoDate || ''}
                onChange={e => setNewAgentForm({ ...newAgentForm, demoDate: e.target.value })}
              />
            </div>
            <div className="idx-form-group">
              <label>UAT Start</label>
              <input 
                type="date" 
                value={newAgentForm.uatStartDate || ''}
                onChange={e => setNewAgentForm({ ...newAgentForm, uatStartDate: e.target.value })}
              />
            </div>
            <div className="idx-form-group">
              <label>UAT End</label>
              <input 
                type="date" 
                value={newAgentForm.uatEndDate || ''}
                onChange={e => setNewAgentForm({ ...newAgentForm, uatEndDate: e.target.value })}
              />
            </div>
            <div className="idx-form-group idx-form-full">
              <label>Description</label>
              <textarea 
                rows={2}
                placeholder="What will this agent do?"
                value={newAgentForm.description || ''}
                onChange={e => setNewAgentForm({ ...newAgentForm, description: e.target.value })}
              />
            </div>
            <div className="idx-form-group idx-form-full">
              <label>Roadmap Notes</label>
              <textarea 
                rows={2}
                placeholder="Development notes, milestones, dependencies..."
                value={newAgentForm.roadmapNotes || ''}
                onChange={e => setNewAgentForm({ ...newAgentForm, roadmapNotes: e.target.value })}
              />
            </div>
          </div>
          <div className="idx-registry-form-actions">
            <button className="idx-btn-secondary" onClick={() => setShowAddAgent(false)}>Cancel</button>
            <button className="idx-btn-primary" onClick={createAgent} disabled={saving}>
              {saving ? <Loader2 size={14} className="idx-spinner" /> : <Plus size={14} />}
              Add Agent
            </button>
          </div>
        </div>
      )}

      {/* Agent Cards */}
      <div className="idx-registry-agents">
        {sortedAgents.map(agent => {
          const isEditing = editingAgent === agent.id;
          const isExpanded = expandedAgent === agent.id;
          const statusCfg = getStatusConfig(agent.status);

          return (
            <div key={agent.id} className={`idx-registry-card ${isEditing ? 'editing' : ''}`}>
              {/* Card Header */}
              <div className="idx-registry-card-header" onClick={() => !isEditing && setExpandedAgent(isExpanded ? null : agent.id)}>
                <div className="idx-registry-card-title">
                  <Bot size={20} style={{ color: 'var(--idx-text-muted)' }} />
                  <div>
                    <h4>{agent.name}</h4>
                    <span className="idx-registry-agent-id">{agent.id}</span>
                  </div>
                  <span className="idx-registry-status-badge" style={{ 
                    color: statusCfg.cssColor, 
                    backgroundColor: statusCfg.bgVar,
                    border: '1px solid var(--idx-border)',
                  }}>
                    {statusCfg.icon} {statusCfg.label}
                  </span>
                  {agent.version && <span className="idx-registry-version">v{agent.version}</span>}
                  {agent.disabled && <span className="idx-registry-disabled-badge">Disabled</span>}
                  {agent.hidden && <span className="idx-registry-hidden-badge">Hidden</span>}
                </div>
                <div className="idx-registry-card-actions">
                  {!isEditing && (
                    <>
                      <button className="idx-btn-icon" title="Edit" onClick={(e) => { e.stopPropagation(); startEditing(agent); }}>
                        <Edit3 size={16} />
                      </button>
                      {(agent.status === 'planned' || agent.isNew) && (
                        <button className="idx-btn-icon idx-btn-danger" title="Delete" onClick={(e) => { e.stopPropagation(); deleteAgent(agent.id); }}>
                          <Trash2 size={16} />
                        </button>
                      )}
                    </>
                  )}
                  {!isEditing && (
                    <ChevronRight 
                      size={18} 
                      className={`idx-expand-icon ${isExpanded ? 'rotated' : ''}`}
                    />
                  )}
                </div>
              </div>

              {/* Card Description (always visible) */}
              <p className="idx-registry-description">{agent.description}</p>

              {/* Edit Form */}
              {isEditing && (
                <div className="idx-registry-edit-form">
                  <div className="idx-registry-form-grid">
                    {/* Row 1: Name + Status */}
                    <div className="idx-form-group">
                      <label>Name</label>
                      <input 
                        type="text" 
                        value={editForm.name || ''}
                        onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                      />
                    </div>
                    <div className="idx-form-group">
                      <label>Status</label>
                      <select 
                        value={editForm.status || 'active'}
                        onChange={e => setEditForm({ ...editForm, status: e.target.value as RegistryAgent['status'] })}
                      >
                        <option value="active">Active</option>
                        <option value="in_development">In Development</option>
                        <option value="uat">UAT</option>
                        <option value="planned">Planned</option>
                        <option value="deprecated">Deprecated</option>
                      </select>
                    </div>

                    {/* Row 2: Version + Planned Date */}
                    <div className="idx-form-group">
                      <label>Version</label>
                      <input 
                        type="text" 
                        value={editForm.version || ''}
                        onChange={e => setEditForm({ ...editForm, version: e.target.value })}
                      />
                    </div>
                    <div className="idx-form-group">
                      <label>Planned Date</label>
                      <input 
                        type="text" 
                        placeholder="e.g. Q2 2026"
                        value={editForm.plannedDate || ''}
                        onChange={e => setEditForm({ ...editForm, plannedDate: e.target.value })}
                      />
                    </div>

                    {/* Version History Editor */}
                    <div className="idx-form-group idx-form-full">
                      <div className="idx-form-section-label">
                        <History size={14} />
                        <span>Version History</span>
                      </div>
                    </div>
                    <div className="idx-form-group idx-form-full">
                      <p style={{ fontSize: '0.8rem', color: 'var(--idx-text-muted)', margin: '0 0 8px' }}>
                        Edit release notes that users see in the version modal. Newest entries appear first.
                      </p>
                      {(editForm.versionHistory || []).slice().reverse().map((entry, revIdx) => {
                        const realIdx = (editForm.versionHistory || []).length - 1 - revIdx;
                        return (
                          <div key={revIdx} style={{ display: 'grid', gridTemplateColumns: '80px 100px 1fr auto', gap: '6px', marginBottom: '6px', alignItems: 'start' }}>
                            <input
                              type="text"
                              placeholder="Version"
                              value={entry.version || ''}
                              onChange={e => {
                                const updated = [...(editForm.versionHistory || [])];
                                updated[realIdx] = { ...updated[realIdx], version: e.target.value };
                                setEditForm({ ...editForm, versionHistory: updated });
                              }}
                              style={{ fontSize: '0.85rem', padding: '4px 8px', fontWeight: 600 }}
                            />
                            <input
                              type="date"
                              value={entry.date || ''}
                              onChange={e => {
                                const updated = [...(editForm.versionHistory || [])];
                                updated[realIdx] = { ...updated[realIdx], date: e.target.value };
                                setEditForm({ ...editForm, versionHistory: updated });
                              }}
                              style={{ fontSize: '0.85rem', padding: '4px 8px' }}
                            />
                            <input
                              type="text"
                              placeholder="Release notes (visible to users)"
                              value={entry.notes || ''}
                              onChange={e => {
                                const updated = [...(editForm.versionHistory || [])];
                                updated[realIdx] = { ...updated[realIdx], notes: e.target.value };
                                setEditForm({ ...editForm, versionHistory: updated });
                              }}
                              style={{ fontSize: '0.85rem', padding: '4px 8px' }}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const updated = [...(editForm.versionHistory || [])];
                                updated.splice(realIdx, 1);
                                setEditForm({ ...editForm, versionHistory: updated });
                              }}
                              style={{ background: 'none', border: 'none', color: 'var(--idx-text-error, #ef4444)', cursor: 'pointer', padding: '4px' }}
                              title="Remove version entry"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => {
                          const vh = [...(editForm.versionHistory || [])];
                          vh.push({
                            version: editForm.version || '1.0.0',
                            date: new Date().toISOString().split('T')[0],
                            notes: '',
                          });
                          setEditForm({ ...editForm, versionHistory: vh });
                        }}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          fontSize: '0.8rem', padding: '4px 10px', marginTop: '4px',
                          border: '1px dashed var(--idx-border)', borderRadius: '6px',
                          background: 'transparent', color: 'var(--idx-text-secondary)', cursor: 'pointer',
                        }}
                      >
                        <Plus size={12} /> Add Version Entry
                      </button>
                    </div>

                    {/* Home-screen Update Notice / What's New */}
                    <div className="idx-form-group idx-form-full">
                      <div className="idx-form-section-label">
                        <Star size={14} />
                        <span>Home-screen Update Notice / What's New</span>
                      </div>
                    </div>
                    <div className="idx-form-group idx-form-full">
                      <p style={{ fontSize: '0.8rem', color: 'var(--idx-text-muted)', margin: '0 0 8px' }}>
                        When enabled, this agent shows an <strong>UPDATED</strong> badge and floats to the top
                        of the home screen for every user until they next open it. The "What's New" text is
                        also shown as a dated banner in the agent's intro message.
                      </p>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', marginBottom: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={!!editForm.updateNotice?.enabled}
                          onChange={e => setEditForm({
                            ...editForm,
                            updateNotice: { ...(editForm.updateNotice || {}), enabled: e.target.checked },
                          })}
                        />
                        <span>Announce update on home screen</span>
                      </label>
                      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '8px', alignItems: 'start' }}>
                        <div>
                          <label style={{ fontSize: '0.75rem', color: 'var(--idx-text-muted)', display: 'block', marginBottom: '2px' }}>Status date</label>
                          <input
                            type="date"
                            value={editForm.updateNotice?.date || ''}
                            onChange={e => setEditForm({
                              ...editForm,
                              updateNotice: { ...(editForm.updateNotice || { enabled: false }), date: e.target.value },
                            })}
                            style={{ fontSize: '0.85rem', padding: '4px 8px', width: '100%' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '0.75rem', color: 'var(--idx-text-muted)', display: 'block', marginBottom: '2px' }}>What's New (shown in intro; Markdown ok)</label>
                          <textarea
                            rows={3}
                            placeholder={"e.g. Added 200 new documents covering billing automation, price increases, and cash application."}
                            value={editForm.updateNotice?.summary || ''}
                            onChange={e => setEditForm({
                              ...editForm,
                              updateNotice: { ...(editForm.updateNotice || { enabled: false }), summary: e.target.value },
                            })}
                            style={{ fontSize: '0.85rem', padding: '6px 8px', width: '100%', resize: 'vertical' }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Release Lifecycle Section */}
                    <div className="idx-form-group idx-form-full">
                      <div className="idx-form-section-label">
                        <Calendar size={14} />
                        <span>Release Lifecycle</span>
                      </div>
                    </div>
                    <div className="idx-form-group">
                      <label><Tv size={12} /> Demo Scheduled</label>
                      <input 
                        type="date" 
                        value={editForm.demoDate || ''}
                        onChange={e => setEditForm({ ...editForm, demoDate: e.target.value })}
                      />
                    </div>
                    <div className="idx-form-group">
                      <label><FlaskConical size={12} /> UAT Start</label>
                      <input 
                        type="date" 
                        value={editForm.uatStartDate || ''}
                        onChange={e => setEditForm({ ...editForm, uatStartDate: e.target.value })}
                      />
                    </div>
                    <div className="idx-form-group">
                      <label><FlaskConical size={12} /> UAT End</label>
                      <input 
                        type="date" 
                        value={editForm.uatEndDate || ''}
                        onChange={e => setEditForm({ ...editForm, uatEndDate: e.target.value })}
                      />
                    </div>
                    <div className="idx-form-group">
                      <label><Rocket size={12} /> Release Date</label>
                      <input 
                        type="date" 
                        value={editForm.releaseDate || ''}
                        onChange={e => setEditForm({ ...editForm, releaseDate: e.target.value })}
                      />
                    </div>

                    {/* Contacts Section */}
                    <div className="idx-form-group idx-form-full">
                      <div className="idx-form-section-label">
                        <Mail size={14} />
                        <span>Agent Contacts</span>
                      </div>
                    </div>
                    <div className="idx-form-group idx-form-full">
                      <p style={{ fontSize: '0.8rem', color: 'var(--idx-text-muted)', margin: '0 0 8px' }}>
                        People Aldo can refer users to for this agent's domain. These contacts appear in Aldo's system prompt.
                      </p>
                      {(editForm.contacts || []).map((contact, cIdx) => (
                        <div key={cIdx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
                          <input
                            type="text"
                            placeholder="Name"
                            value={contact.name || ''}
                            onChange={e => {
                              const updated = [...(editForm.contacts || [])];
                              updated[cIdx] = { ...updated[cIdx], name: e.target.value };
                              setEditForm({ ...editForm, contacts: updated });
                            }}
                            style={{ fontSize: '0.85rem', padding: '4px 8px' }}
                          />
                          <input
                            type="text"
                            placeholder="Role"
                            value={contact.role || ''}
                            onChange={e => {
                              const updated = [...(editForm.contacts || [])];
                              updated[cIdx] = { ...updated[cIdx], role: e.target.value };
                              setEditForm({ ...editForm, contacts: updated });
                            }}
                            style={{ fontSize: '0.85rem', padding: '4px 8px' }}
                          />
                          <input
                            type="email"
                            placeholder="Email"
                            value={contact.email || ''}
                            onChange={e => {
                              const updated = [...(editForm.contacts || [])];
                              updated[cIdx] = { ...updated[cIdx], email: e.target.value };
                              setEditForm({ ...editForm, contacts: updated });
                            }}
                            style={{ fontSize: '0.85rem', padding: '4px 8px' }}
                          />
                          <input
                            type="text"
                            placeholder="Context (when to contact)"
                            value={contact.context || ''}
                            onChange={e => {
                              const updated = [...(editForm.contacts || [])];
                              updated[cIdx] = { ...updated[cIdx], context: e.target.value };
                              setEditForm({ ...editForm, contacts: updated });
                            }}
                            style={{ fontSize: '0.85rem', padding: '4px 8px' }}
                          />
                          <button
                            onClick={() => {
                              const updated = (editForm.contacts || []).filter((_, i) => i !== cIdx);
                              setEditForm({ ...editForm, contacts: updated });
                            }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '4px' }}
                            title="Remove contact"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          const updated = [...(editForm.contacts || []), { name: '', role: '', email: '', context: '' }];
                          setEditForm({ ...editForm, contacts: updated });
                        }}
                        className="idx-refresh-btn"
                        style={{ marginTop: '4px', fontSize: '0.8rem' }}
                      >
                        <Plus size={12} /> Add Contact
                      </button>
                    </div>

                    {/* Work Items Section */}
                    <div className="idx-form-group idx-form-full">
                      <div className="idx-form-section-label">
                        <ClipboardList size={14} />
                        <span>Work Items</span>
                      </div>
                    </div>
                    <div className="idx-form-group idx-form-full">
                      <p style={{ fontSize: '0.8rem', color: 'var(--idx-text-muted)', margin: '0 0 8px' }}>
                        Tasks and backlog items for this agent. Items with dates appear in the roadmap timeline.
                      </p>
                      {(editForm.workItems || []).map((item, wIdx) => (
                        <div key={wIdx} style={{ display: 'grid', gridTemplateColumns: '2fr 120px 110px 2fr auto', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
                          <input
                            type="text"
                            placeholder="Title"
                            value={item.title || ''}
                            onChange={e => {
                              const updated = [...(editForm.workItems || [])];
                              updated[wIdx] = { ...updated[wIdx], title: e.target.value };
                              setEditForm({ ...editForm, workItems: updated });
                            }}
                            style={{ fontSize: '0.85rem', padding: '4px 8px' }}
                          />
                          <input
                            type="date"
                            value={item.date || ''}
                            onChange={e => {
                              const updated = [...(editForm.workItems || [])];
                              updated[wIdx] = { ...updated[wIdx], date: e.target.value };
                              setEditForm({ ...editForm, workItems: updated });
                            }}
                            style={{ fontSize: '0.85rem', padding: '4px 8px' }}
                          />
                          <select
                            value={item.status || 'pending'}
                            onChange={e => {
                              const updated = [...(editForm.workItems || [])];
                              updated[wIdx] = { ...updated[wIdx], status: e.target.value as 'pending' | 'in_progress' | 'done' };
                              setEditForm({ ...editForm, workItems: updated });
                            }}
                            style={{ fontSize: '0.85rem', padding: '4px 8px' }}
                          >
                            <option value="pending">Pending</option>
                            <option value="in_progress">In Progress</option>
                            <option value="done">Done</option>
                          </select>
                          <input
                            type="text"
                            placeholder="Notes (optional)"
                            value={item.notes || ''}
                            onChange={e => {
                              const updated = [...(editForm.workItems || [])];
                              updated[wIdx] = { ...updated[wIdx], notes: e.target.value };
                              setEditForm({ ...editForm, workItems: updated });
                            }}
                            style={{ fontSize: '0.85rem', padding: '4px 8px' }}
                          />
                          <button
                            onClick={() => {
                              const updated = (editForm.workItems || []).filter((_, i) => i !== wIdx);
                              setEditForm({ ...editForm, workItems: updated });
                            }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '4px' }}
                            title="Remove work item"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          const updated = [...(editForm.workItems || []), { title: '', date: '', status: 'pending' as const, notes: '' }];
                          setEditForm({ ...editForm, workItems: updated });
                        }}
                        className="idx-refresh-btn"
                        style={{ marginTop: '4px', fontSize: '0.8rem' }}
                      >
                        <Plus size={12} /> Add Work Item
                      </button>
                    </div>

                    {/* Access Channels Section */}
                    <div className="idx-form-group idx-form-full">
                      <div className="idx-form-section-label">
                        <Radio size={14} />
                        <span>Access Channels</span>
                      </div>
                    </div>
                    <div className="idx-form-group idx-form-full">
                      <p style={{ fontSize: '0.8rem', color: 'var(--idx-text-muted)', margin: '0 0 8px' }}>
                        Alternative surfaces where this agent is available (e.g. embedded widgets, direct links). Enabled channels are injected into the agent's system prompt.
                      </p>
                      {(editForm.accessChannels || []).map((channel, chIdx) => (
                        <div key={chIdx} style={{
                          display: 'grid', gridTemplateColumns: '100px 1fr 1fr 1fr 80px auto auto',
                          gap: '6px', marginBottom: '6px', alignItems: 'center'
                        }}>
                          <select
                            value={channel.type || 'widget'}
                            onChange={e => {
                              const updated = [...(editForm.accessChannels || [])];
                              updated[chIdx] = { ...updated[chIdx], type: e.target.value as 'widget' | 'embed' | 'link' | 'app' };
                              setEditForm({ ...editForm, accessChannels: updated });
                            }}
                            style={{ fontSize: '0.85rem', padding: '4px 8px' }}
                          >
                            <option value="widget">Widget</option>
                            <option value="embed">Embed</option>
                            <option value="link">Link</option>
                            <option value="app">App</option>
                          </select>
                          <input
                            type="text"
                            placeholder="Label (e.g. PWMS In-App Widget)"
                            value={channel.label || ''}
                            onChange={e => {
                              const updated = [...(editForm.accessChannels || [])];
                              updated[chIdx] = { ...updated[chIdx], label: e.target.value };
                              setEditForm({ ...editForm, accessChannels: updated });
                            }}
                            style={{ fontSize: '0.85rem', padding: '4px 8px' }}
                          />
                          <input
                            type="text"
                            placeholder="Description"
                            value={channel.description || ''}
                            onChange={e => {
                              const updated = [...(editForm.accessChannels || [])];
                              updated[chIdx] = { ...updated[chIdx], description: e.target.value };
                              setEditForm({ ...editForm, accessChannels: updated });
                            }}
                            style={{ fontSize: '0.85rem', padding: '4px 8px' }}
                          />
                          <input
                            type="url"
                            placeholder="URL"
                            value={channel.url || ''}
                            onChange={e => {
                              const updated = [...(editForm.accessChannels || [])];
                              updated[chIdx] = { ...updated[chIdx], url: e.target.value };
                              setEditForm({ ...editForm, accessChannels: updated });
                            }}
                            style={{ fontSize: '0.85rem', padding: '4px 8px' }}
                          />
                          <input
                            type="text"
                            placeholder="Placement"
                            value={channel.placement || ''}
                            onChange={e => {
                              const updated = [...(editForm.accessChannels || [])];
                              updated[chIdx] = { ...updated[chIdx], placement: e.target.value };
                              setEditForm({ ...editForm, accessChannels: updated });
                            }}
                            style={{ fontSize: '0.85rem', padding: '4px 8px' }}
                          />
                          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            <input
                              type="checkbox"
                              checked={channel.enabled !== false}
                              onChange={e => {
                                const updated = [...(editForm.accessChannels || [])];
                                updated[chIdx] = { ...updated[chIdx], enabled: e.target.checked };
                                setEditForm({ ...editForm, accessChannels: updated });
                              }}
                            />
                            On
                          </label>
                          <button
                            onClick={() => {
                              const updated = (editForm.accessChannels || []).filter((_, i) => i !== chIdx);
                              setEditForm({ ...editForm, accessChannels: updated });
                            }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '4px' }}
                            title="Remove channel"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          const newId = `channel-${Date.now()}`;
                          const updated = [...(editForm.accessChannels || []), {
                            id: newId, type: 'widget' as const, label: '', description: '', url: '', placement: '', enabled: true
                          }];
                          setEditForm({ ...editForm, accessChannels: updated });
                        }}
                        className="idx-refresh-btn"
                        style={{ marginTop: '4px', fontSize: '0.8rem' }}
                      >
                        <Plus size={12} /> Add Channel
                      </button>
                    </div>

                    {/* Access Control Section — SharePoint site membership gating */}
                    <div className="idx-form-group idx-form-full">
                      <div className="idx-form-section-label">
                        <Lock size={14} />
                        <span>Access Control</span>
                      </div>
                    </div>
                    <div className="idx-form-group idx-form-full">
                      <p style={{ fontSize: '0.8rem', color: 'var(--idx-text-muted)', margin: '0 0 8px' }}>
                        Gate this agent by <strong>SharePoint site membership</strong>. Any member of the site below
                        is granted access (checked in real time via Microsoft Graph). Leave blank for no SharePoint
                        gating. This is additive — it never revokes role- or default-based access.
                      </p>
                      <label>SharePoint Site URL</label>
                      <input
                        type="url"
                        placeholder="https://unifirst.sharepoint.com/sites/AskIRIS"
                        value={editForm.accessControl?.sharePointSiteUrl || ''}
                        onChange={e => {
                          const next = { ...(editForm.accessControl || {}), sharePointSiteUrl: e.target.value };
                          const hasValue = !!(next.sharePointSiteUrl?.trim() || next.sharePointSiteId?.trim());
                          setEditForm({ ...editForm, accessControl: hasValue ? next : null });
                        }}
                      />
                      <label style={{ marginTop: '8px', display: 'block' }}>
                        Graph Site ID <span className="idx-form-hint"> — optional, advanced (host,siteCollectionId,webId); takes precedence over URL</span>
                      </label>
                      <input
                        type="text"
                        placeholder="unifirst.sharepoint.com,23747a57-...,33cd54fa-..."
                        value={editForm.accessControl?.sharePointSiteId || ''}
                        onChange={e => {
                          const next = { ...(editForm.accessControl || {}), sharePointSiteId: e.target.value };
                          const hasValue = !!(next.sharePointSiteUrl?.trim() || next.sharePointSiteId?.trim());
                          setEditForm({ ...editForm, accessControl: hasValue ? next : null });
                        }}
                        className="idx-textarea-monospace"
                      />
                    </div>

                    {/* Skills Section — agent capabilities (A2A AgentSkill schema) */}
                    <div className="idx-form-group idx-form-full">
                      <div className="idx-form-section-label">
                        <Zap size={14} />
                        <span>Skills / Capabilities</span>
                      </div>
                    </div>
                    <div className="idx-form-group idx-form-full">
                      <p style={{ fontSize: '0.8rem', color: 'var(--idx-text-muted)', margin: '0 0 8px' }}>
                        Declared capabilities for the concierge orchestrator, MCP, and discovery — modeled on the
                        <strong> A2A AgentSkill</strong> schema. Ongoing process: add/refine skills over time. Tags and
                        examples are comma-separated.
                      </p>
                      {(editForm.skills || []).map((skill, sIdx) => (
                        <div key={sIdx} style={{ border: '1px solid var(--idx-border)', borderRadius: '6px', padding: '8px', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                            <input
                              style={{ flex: 1 }}
                              placeholder="id (kebab-case, e.g. product-search)"
                              value={skill.id || ''}
                              onChange={e => {
                                const updated = [...(editForm.skills || [])];
                                updated[sIdx] = { ...updated[sIdx], id: e.target.value };
                                setEditForm({ ...editForm, skills: updated });
                              }}
                              className="idx-textarea-monospace"
                            />
                            <input
                              style={{ flex: 1 }}
                              placeholder="name (e.g. Product Search)"
                              value={skill.name || ''}
                              onChange={e => {
                                const updated = [...(editForm.skills || [])];
                                updated[sIdx] = { ...updated[sIdx], name: e.target.value };
                                setEditForm({ ...editForm, skills: updated });
                              }}
                            />
                            <button
                              className="idx-btn-icon"
                              title="Remove skill"
                              onClick={() => {
                                const updated = (editForm.skills || []).filter((_, i) => i !== sIdx);
                                setEditForm({ ...editForm, skills: updated });
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                          <textarea
                            rows={2}
                            placeholder="description — what this skill does (be specific for routing)"
                            value={skill.description || ''}
                            onChange={e => {
                              const updated = [...(editForm.skills || [])];
                              updated[sIdx] = { ...updated[sIdx], description: e.target.value };
                              setEditForm({ ...editForm, skills: updated });
                            }}
                            style={{ width: '100%', marginBottom: '6px' }}
                          />
                          <input
                            style={{ width: '100%', marginBottom: '6px' }}
                            placeholder="tags (comma-separated, e.g. search, catalog, products)"
                            value={(skill.tags || []).join(', ')}
                            onChange={e => {
                              const updated = [...(editForm.skills || [])];
                              updated[sIdx] = { ...updated[sIdx], tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) };
                              setEditForm({ ...editForm, skills: updated });
                            }}
                          />
                          <input
                            style={{ width: '100%' }}
                            placeholder="examples (comma-separated sample prompts)"
                            value={(skill.examples || []).join(', ')}
                            onChange={e => {
                              const updated = [...(editForm.skills || [])];
                              updated[sIdx] = { ...updated[sIdx], examples: e.target.value.split(',').map(t => t.trim()).filter(Boolean) };
                              setEditForm({ ...editForm, skills: updated });
                            }}
                          />
                        </div>
                      ))}
                      <button
                        className="idx-btn-secondary idx-btn-sm"
                        onClick={() => {
                          const newSkill: AgentSkill = { id: '', name: '', description: '', tags: [], examples: [] };
                          setEditForm({ ...editForm, skills: [...(editForm.skills || []), newSkill] });
                        }}
                      >
                        <Plus size={14} /> Add Skill
                      </button>
                    </div>

                    {/* Description + Roadmap */}
                    <div className="idx-form-group idx-form-full">
                      <label>Description</label>
                      <textarea 
                        rows={3}
                        value={editForm.description || ''}
                        onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                      />
                    </div>
                    <div className="idx-form-group idx-form-full">
                      <label>Roadmap Notes</label>
                      <textarea 
                        rows={2}
                        placeholder="Development notes, milestones, dependencies..."
                        value={editForm.roadmapNotes || ''}
                        onChange={e => setEditForm({ ...editForm, roadmapNotes: e.target.value })}
                      />
                    </div>

                    {/* Sample Questions */}
                    <div className="idx-form-group idx-form-full">
                      <label>
                        Sample Questions
                        <button className="idx-btn-add-sm" onClick={() => addSampleQuestion(editForm, setEditForm)}>
                          <Plus size={12} /> Add
                        </button>
                      </label>
                      {(editForm.sampleQuestions || []).map((q, idx) => (
                        <div key={idx} className="idx-sample-question-row">
                          <input 
                            type="text"
                            value={q}
                            placeholder="Enter a sample question..."
                            onChange={e => updateSampleQuestion(editForm, setEditForm, idx, e.target.value)}
                          />
                          <button className="idx-btn-icon idx-btn-danger" onClick={() => removeSampleQuestion(editForm, setEditForm, idx)}>
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Routing Tips (Markdown — used in Aldo's system prompt) */}
                    <div className="idx-form-group idx-form-full">
                      <label>
                        Routing Tips
                        <span className="idx-form-hint"> — Markdown instructions for Aldo's routing decisions</span>
                      </label>
                      <textarea 
                        rows={6}
                        placeholder="### Agent Name&#10;**Use for questions about:**&#10;- Topic 1&#10;- Topic 2"
                        value={editForm.routingTips || ''}
                        onChange={e => setEditForm({ ...editForm, routingTips: e.target.value })}
                        className="idx-textarea-monospace"
                      />
                    </div>

                    {/* Intro / Welcome Message (Markdown — shown to user on agent selection) */}
                    <div className="idx-form-group idx-form-full">
                      <label>
                        Intro Message
                        <span className="idx-form-hint"> — Welcome message shown when agent is selected</span>
                      </label>
                      <textarea 
                        rows={6}
                        placeholder="Hello! I'm your **Agent Name** assistant.&#10;&#10;I can help you with..."
                        value={editForm.introMessage || ''}
                        onChange={e => setEditForm({ ...editForm, introMessage: e.target.value })}
                        className="idx-textarea-monospace"
                      />
                    </div>

                    {/* Flags */}
                    <div className="idx-form-group">
                      <label className="idx-checkbox-label">
                        <input 
                          type="checkbox" 
                          checked={editForm.featured || false}
                          onChange={e => setEditForm({ ...editForm, featured: e.target.checked })}
                        />
                        Featured in Dropdown
                      </label>
                    </div>
                    <div className="idx-form-group">
                      <label className="idx-checkbox-label">
                        <input 
                          type="checkbox" 
                          checked={editForm.disabled || false}
                          onChange={e => setEditForm({ ...editForm, disabled: e.target.checked })}
                        />
                        Disabled
                      </label>
                    </div>
                    <div className="idx-form-group">
                      <label className="idx-checkbox-label">
                        <input 
                          type="checkbox" 
                          checked={editForm.hidden || false}
                          onChange={e => setEditForm({ ...editForm, hidden: e.target.checked })}
                        />
                        Hidden from UI
                      </label>
                    </div>
                  </div>
                  <div className="idx-registry-form-actions">
                    <button className="idx-btn-secondary" onClick={cancelEditing}>Cancel</button>
                    <button className="idx-btn-primary" onClick={() => saveAgent(agent.id)} disabled={saving}>
                      {saving ? <Loader2 size={14} className="idx-spinner" /> : <Save size={14} />}
                      Save Changes
                    </button>
                  </div>
                </div>
              )}

              {/* Expanded Details */}
              {isExpanded && !isEditing && (
                <div className="idx-registry-details">
                  {/* Knowledge Base & Content Sources — shown first for ground truth visibility */}
                  {(() => {
                    const kb = agent.knowledgeBase;
                    const KB_TYPE_LABELS: Record<string, { label: string; icon: React.ReactNode; bgVar: string }> = {
                      askaldokb: { label: 'AskAldoKB (Managed)', icon: <Archive size={13} />, bgVar: 'var(--idx-bg-success)' },
                      external_sharepoint: { label: 'External SharePoint', icon: <Globe size={13} />, bgVar: 'var(--idx-bg-warn)' },
                      standalone_document: { label: 'Standalone Document', icon: <FileText size={13} />, bgVar: 'var(--idx-bg-success)' },
                      live_api: { label: 'Live API', icon: <Zap size={13} />, bgVar: 'var(--idx-bg-inset)' },
                    };

                    return (
                      <div className="idx-registry-section">
                        <h5><Database size={14} /> Knowledge Base</h5>
                        {!kb ? (
                          <div style={{ fontSize: '0.85rem', color: 'var(--idx-text-muted)', padding: '8px 12px', background: 'var(--idx-bg-secondary)', borderRadius: '8px' }}>
                            No knowledge base configured.
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {/* KB Type + Index badge row */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                              {(() => {
                                const cfg = KB_TYPE_LABELS[kb.type] || { label: kb.type, icon: <HardDrive size={13} />, bgVar: 'var(--idx-bg-tertiary)' };
                                return (
                                  <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                    fontSize: '0.78rem', padding: '3px 10px', borderRadius: '12px',
                                    color: 'var(--idx-text-primary)', backgroundColor: cfg.bgVar,
                                    border: '1px solid var(--idx-border)', fontWeight: 500,
                                  }}>
                                    {cfg.icon} {cfg.label}
                                  </span>
                                );
                              })()}
                              {kb.indexName && (
                                <span style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                                  fontSize: '0.78rem', padding: '3px 10px', borderRadius: '12px',
                                  color: 'var(--idx-text-mono)', backgroundColor: 'var(--idx-bg-tertiary)', border: '1px solid var(--idx-border)',
                                  fontFamily: 'monospace',
                                }}>
                                  <HardDrive size={11} /> {kb.indexName}
                                </span>
                              )}
                              {kb.indexProvider && (
                                <span style={{
                                  fontSize: '0.75rem', color: 'var(--idx-text-faint)',
                                }}>
                                  via {kb.indexProvider.replace(/_/g, ' ')}
                                </span>
                              )}
                              {kb.documentCount && (
                                <span style={{ fontSize: '0.75rem', color: 'var(--idx-text-muted)' }}>
                                  ({kb.documentCount.toLocaleString()} docs)
                                </span>
                              )}
                            </div>

                            {/* Per-environment indexes (collapsed) */}
                            {kb.environments && Object.keys(kb.environments).length > 1 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {Object.entries(kb.environments).map(([env, idx]) => (
                                  <span key={env} style={{
                                    fontSize: '0.72rem', padding: '2px 8px', borderRadius: '8px',
                                    background: 'var(--idx-bg-secondary)', border: '1px solid var(--idx-border)', fontFamily: 'monospace',
                                    color: 'var(--idx-text-muted)',
                                  }}>
                                    <strong>{env.toUpperCase()}</strong>: {idx}
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* Content Sources */}
                            {kb.contentSources && kb.contentSources.length > 0 && (
                              <div>
                                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--idx-text-secondary)', marginBottom: '6px' }}>
                                  Content Sources
                                </div>
                                {kb.contentSources.map((src, sIdx) => (
                                  <div key={sIdx} style={{
                                    display: 'flex', flexDirection: 'column', gap: '3px',
                                    padding: '8px 12px', background: 'var(--idx-bg-secondary)', borderRadius: '8px',
                                    border: src.isPrimary ? '2px solid var(--idx-border-strong)' : '1px solid var(--idx-border)',
                                    marginBottom: '6px',
                                  }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      {src.type === 'external_sharepoint' ? <Globe size={13} style={{ color: 'var(--idx-text-muted)' }} /> :
                                       src.type === 'live_api' ? <Zap size={13} style={{ color: 'var(--idx-text-link)' }} /> :
                                       src.type === 'standalone_document' ? <FileText size={13} style={{ color: 'var(--idx-text-muted)' }} /> :
                                       <Archive size={13} style={{ color: 'var(--idx-text-muted)' }} />}
                                      <strong style={{ fontSize: '0.85rem' }}>{src.name}</strong>
                                      {src.isPrimary && (
                                        <span style={{ fontSize: '0.7rem', padding: '1px 6px', borderRadius: '8px', background: 'var(--idx-bg-success)', color: 'var(--idx-text-primary)', fontWeight: 600, border: '1px solid var(--idx-border)' }}>
                                          PRIMARY
                                        </span>
                                      )}
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: 'var(--idx-text-muted)' }}>{src.description}</div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--idx-text-faint)', fontFamily: 'monospace' }}>
                                      {src.location}
                                      {src.url && (
                                        <a href={src.url} target="_blank" rel="noopener noreferrer"
                                          style={{ marginLeft: '8px', color: 'var(--idx-text-link)' }}
                                        >
                                          <ExternalLink size={10} style={{ display: 'inline', verticalAlign: 'middle' }} />
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Sync Configuration */}
                            {kb.sync && (
                              <div style={{
                                padding: '8px 12px', borderRadius: '8px',
                                background: kb.sync.automated ? 'var(--idx-bg-success)' : 'var(--idx-bg-warn)',
                                border: '1px solid var(--idx-border)',
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                  {kb.sync.automated
                                    ? <><Zap size={13} style={{ color: 'var(--idx-text-primary)' }} /> <strong style={{ fontSize: '0.82rem', color: 'var(--idx-text-primary)' }}>Automated Sync</strong></>
                                    : <><Clock size={13} style={{ color: 'var(--idx-text-primary)' }} /> <strong style={{ fontSize: '0.82rem', color: 'var(--idx-text-primary)' }}>Manual</strong></>
                                  }
                                  {kb.sync.schedule && (
                                    <span style={{ fontSize: '0.75rem', color: 'var(--idx-text-muted)' }}>
                                      <Clock size={10} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '3px' }} />
                                      {kb.sync.schedule}
                                    </span>
                                  )}
                                </div>
                                {kb.sync.pipeline && (
                                  <div style={{ fontSize: '0.75rem', color: 'var(--idx-text-muted)', fontFamily: 'monospace' }}>
                                    Pipeline: {kb.sync.pipeline}
                                  </div>
                                )}
                                {kb.sync.functionApp && (
                                  <div style={{ fontSize: '0.75rem', color: 'var(--idx-text-muted)', fontFamily: 'monospace' }}>
                                    Function App: {kb.sync.functionApp}
                                  </div>
                                )}
                                {kb.sync.hubAndSpoke?.enabled && (
                                  <div style={{ fontSize: '0.75rem', color: 'var(--idx-text-primary)', marginTop: '4px' }}>
                                    <Link2 size={10} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '3px' }} />
                                    Hub-and-spoke: {kb.sync.hubAndSpoke.targetFolder} ({kb.sync.hubAndSpoke.syncSchedule})
                                  </div>
                                )}
                                <div style={{ fontSize: '0.75rem', color: 'var(--idx-text-faint)', marginTop: '4px', fontStyle: 'italic' }}>
                                  {kb.sync.notes}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Sample Questions */}
                  {agent.sampleQuestions && agent.sampleQuestions.length > 0 && (
                    <div className="idx-registry-section">
                      <h5><MessageSquare size={14} /> Sample Questions</h5>
                      <ul className="idx-registry-questions">
                        {agent.sampleQuestions.map((q, idx) => (
                          <li key={idx}>{q}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Roadmap Notes */}
                  {agent.roadmapNotes && (
                    <div className="idx-registry-section">
                      <h5><Rocket size={14} /> Roadmap Notes</h5>
                      <p>{agent.roadmapNotes}</p>
                    </div>
                  )}

                  {/* Routing Tips */}
                  {agent.routingTips && (
                    <div className="idx-registry-section">
                      <h5><Navigation size={14} /> Routing Tips</h5>
                      <pre className="idx-routing-tips-preview">{agent.routingTips}</pre>
                    </div>
                  )}

                  {/* Intro Message */}
                  {agent.introMessage && (
                    <div className="idx-registry-section">
                      <h5><MessageSquare size={14} /> Intro Message</h5>
                      <pre className="idx-intro-message-preview">{agent.introMessage.substring(0, 200)}{agent.introMessage.length > 200 ? '...' : ''}</pre>
                    </div>
                  )}

                  {/* Featured Status */}
                  <div className="idx-registry-section">
                    <h5><Star size={14} /> Display</h5>
                    <div className="idx-registry-tech-grid">
                      <div className="idx-tech-item">
                        <span className="idx-tech-label">Featured</span>
                        <span>{agent.featured ? <><Star size={12} /> Yes</> : 'No'}</span>
                      </div>
                      <div className="idx-tech-item">
                        <span className="idx-tech-label">Has Routing Tips</span>
                        <span>{agent.routingTips ? <><CheckCircle size={12} /> Yes</> : <><XCircle size={12} /> No</>}</span>
                      </div>
                      <div className="idx-tech-item">
                        <span className="idx-tech-label">Has Intro Message</span>
                        <span>{agent.introMessage ? <><CheckCircle size={12} /> Yes</> : <><XCircle size={12} /> No</>}</span>
                      </div>
                      <div className="idx-tech-item">
                        <span className="idx-tech-label">Access Channels</span>
                        <span>{agent.accessChannels && agent.accessChannels.length > 0 ? <><Radio size={12} /> {agent.accessChannels.filter(c => c.enabled).length} active</> : <><XCircle size={12} /> None</>}</span>
                      </div>
                    </div>
                  </div>

                  {/* Technical Details */}
                  <div className="idx-registry-section">
                    <h5><Code size={14} /> Technical Details</h5>
                    <div className="idx-registry-tech-grid">
                      {agent.endpoint && (
                        <div className="idx-tech-item">
                          <span className="idx-tech-label">Endpoint</span>
                          <code>{agent.endpoint}</code>
                        </div>
                      )}
                      {agent.streamingEndpoint && (
                        <div className="idx-tech-item">
                          <span className="idx-tech-label">Streaming</span>
                          <code>{agent.streamingEndpoint}</code>
                        </div>
                      )}
                      {agent.healthEndpoint && (
                        <div className="idx-tech-item">
                          <span className="idx-tech-label">Health</span>
                          <code>{agent.healthEndpoint}</code>
                        </div>
                      )}
                      <div className="idx-tech-item">
                        <span className="idx-tech-label">Streaming</span>
                        <span>{agent.isStreaming ? <><CheckCircle size={12} /> Yes</> : <><XCircle size={12} /> No</>}</span>
                      </div>
                      <div className="idx-tech-item">
                        <span className="idx-tech-label">A2A Protocol</span>
                        <span>{agent.supportsA2A ? <><CheckCircle size={12} /> Yes</> : <><XCircle size={12} /> No</>}</span>
                      </div>
                    </div>
                  </div>

                  {/* Release Lifecycle */}
                  {(agent.plannedDate || agent.releaseDate || agent.demoDate || agent.uatStartDate || agent.uatEndDate) && (
                    <div className="idx-registry-section">
                      <h5><Calendar size={14} /> Release Lifecycle</h5>
                      <div className="idx-registry-tech-grid">
                        {agent.plannedDate && (
                          <div className="idx-tech-item">
                            <span className="idx-tech-label">Target</span>
                            <span>{agent.plannedDate}</span>
                          </div>
                        )}
                        {agent.demoDate && (
                          <div className="idx-tech-item">
                            <span className="idx-tech-label">Demo</span>
                            <span>{agent.demoDate}</span>
                          </div>
                        )}
                        {(agent.uatStartDate || agent.uatEndDate) && (
                          <div className="idx-tech-item">
                            <span className="idx-tech-label">UAT Window</span>
                            <span>{agent.uatStartDate || '?'}{agent.uatEndDate ? ` – ${agent.uatEndDate}` : ''}</span>
                          </div>
                        )}
                        {agent.releaseDate && (
                          <div className="idx-tech-item">
                            <span className="idx-tech-label">Release</span>
                            <span>{agent.releaseDate}</span>
                          </div>
                        )}
                        {agent.lastUpdated && (
                          <div className="idx-tech-item">
                            <span className="idx-tech-label">Last Updated</span>
                            <span>{new Date(agent.lastUpdated).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Contacts */}
                  {agent.contacts && agent.contacts.length > 0 && (
                    <div className="idx-registry-section">
                      <h5><Mail size={14} /> Contacts</h5>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {agent.contacts.map((c, cIdx) => (
                          <div key={cIdx} style={{ display: 'flex', gap: '12px', fontSize: '0.85rem', padding: '6px 8px', background: 'var(--idx-bg-secondary)', borderRadius: '6px' }}>
                            <strong>{c.name}</strong>
                            <span style={{ color: 'var(--idx-text-muted)' }}>{c.role}</span>
                            <a href={`mailto:${c.email}`} style={{ color: 'var(--idx-text-link)' }}>{c.email}</a>
                            {c.context && <span style={{ color: 'var(--idx-text-faint)', fontStyle: 'italic' }}>{c.context}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Access Channels */}
                  {agent.accessChannels && agent.accessChannels.length > 0 && (
                    <div className="idx-registry-section">
                      <h5><Radio size={14} /> Access Channels</h5>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {agent.accessChannels.map((ch, chIdx) => (
                          <div key={chIdx} style={{
                            display: 'flex', gap: '12px', fontSize: '0.85rem', padding: '6px 8px',
                            background: 'var(--idx-bg-secondary)', borderRadius: '6px', alignItems: 'center'
                          }}>
                            <span style={{
                              fontSize: '0.7rem', padding: '1px 6px', borderRadius: '4px',
                              backgroundColor: ch.enabled ? 'var(--idx-bg-success)' : 'var(--idx-bg-tertiary)',
                              color: 'var(--idx-text-primary)', border: '1px solid var(--idx-border)',
                              textTransform: 'uppercase', fontWeight: 600, whiteSpace: 'nowrap'
                            }}>
                              {ch.type}
                            </span>
                            <strong>{ch.label}</strong>
                            <span style={{ color: 'var(--idx-text-muted)', flex: 1 }}>{ch.description}</span>
                            {ch.url && (
                              <a href={ch.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--idx-text-link)', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                                <ExternalLink size={12} /> {ch.url.replace(/^https?:\/\//, '').substring(0, 40)}
                              </a>
                            )}
                            <span style={{
                              fontSize: '0.7rem', padding: '1px 6px', borderRadius: '4px',
                              backgroundColor: ch.enabled ? 'var(--idx-bg-success)' : 'var(--idx-bg-error)',
                              color: 'var(--idx-text-primary)', border: '1px solid var(--idx-border)',
                            }}>
                              {ch.enabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Access Control — SharePoint site membership gating */}
                  {agent.accessControl && (agent.accessControl.sharePointSiteUrl || agent.accessControl.sharePointSiteId) && (
                    <div className="idx-registry-section">
                      <h5><Lock size={14} /> Access Control</h5>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.85rem' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '6px 8px', background: 'var(--idx-bg-secondary)', borderRadius: '6px', flexWrap: 'wrap' }}>
                          <span style={{
                            fontSize: '0.7rem', padding: '1px 6px', borderRadius: '4px',
                            backgroundColor: 'var(--idx-bg-warn)', color: 'var(--idx-text-primary)',
                            border: '1px solid var(--idx-border)', textTransform: 'uppercase', fontWeight: 600, whiteSpace: 'nowrap'
                          }}>
                            <Globe size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '3px' }} />
                            SharePoint Membership
                          </span>
                          {agent.accessControl.sharePointSiteUrl && (
                            <a href={agent.accessControl.sharePointSiteUrl} target="_blank" rel="noopener noreferrer"
                              style={{ color: 'var(--idx-text-link)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <ExternalLink size={12} /> {agent.accessControl.sharePointSiteUrl.replace(/^https?:\/\//, '')}
                            </a>
                          )}
                          {agent.accessControl.sharePointSiteId && (
                            <code style={{ fontSize: '0.72rem', color: 'var(--idx-text-muted)' }}>{agent.accessControl.sharePointSiteId}</code>
                          )}
                        </div>
                        <span style={{ fontSize: '0.75rem', color: 'var(--idx-text-faint)', fontStyle: 'italic' }}>
                          Members of this site are granted access (additive; does not revoke role/default access).
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Skills / Capabilities (A2A AgentSkill schema) */}
                  {agent.skills && agent.skills.length > 0 && (
                    <div className="idx-registry-section">
                      <h5><Zap size={14} /> Skills / Capabilities</h5>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {agent.skills.map((skill, sIdx) => (
                          <div key={sIdx} style={{ padding: '8px', background: 'var(--idx-bg-secondary)', borderRadius: '6px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <strong style={{ fontSize: '0.85rem' }}>{skill.name || skill.id}</strong>
                              {skill.id && <code style={{ fontSize: '0.7rem', color: 'var(--idx-text-muted)' }}>{skill.id}</code>}
                            </div>
                            {skill.description && (
                              <div style={{ fontSize: '0.8rem', color: 'var(--idx-text-secondary)', marginTop: '3px' }}>{skill.description}</div>
                            )}
                            {skill.tags && skill.tags.length > 0 && (
                              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '5px' }}>
                                {skill.tags.map((tag, tIdx) => (
                                  <span key={tIdx} style={{
                                    fontSize: '0.68rem', padding: '1px 6px', borderRadius: '4px',
                                    backgroundColor: 'var(--idx-bg-primary)', color: 'var(--idx-text-muted)',
                                    border: '1px solid var(--idx-border)'
                                  }}>{tag}</span>
                                ))}
                              </div>
                            )}
                            {skill.examples && skill.examples.length > 0 && (
                              <div style={{ fontSize: '0.72rem', color: 'var(--idx-text-faint)', fontStyle: 'italic', marginTop: '5px' }}>
                                e.g. {skill.examples.map(ex => `"${ex}"`).join(', ')}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Work Items */}
                  {agent.workItems && agent.workItems.length > 0 && (
                    <div className="idx-registry-section">
                      <h5><ClipboardList size={14} /> Work Items</h5>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {agent.workItems
                          .slice()
                          .sort((a, b) => {
                            if (a.date && b.date) return a.date.localeCompare(b.date);
                            if (a.date) return -1;
                            if (b.date) return 1;
                            return 0;
                          })
                          .map((wi, wiIdx) => (
                          <div key={wiIdx} style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            fontSize: '0.85rem', padding: '6px 8px', background: 'var(--idx-bg-secondary)', borderRadius: '6px',
                            textDecoration: wi.status === 'done' ? 'line-through' : 'none',
                            color: wi.status === 'done' ? 'var(--idx-text-faint)' : 'var(--idx-text-secondary)'
                          }}>
                            <CircleDot size={12} style={{
                              color: 'var(--idx-text-muted)',
                              flexShrink: 0
                            }} />
                            <span style={{ flex: 1 }}>{wi.title}</span>
                            {wi.date && <span style={{ color: 'var(--idx-text-muted)', fontSize: '0.8rem', whiteSpace: 'nowrap' }}><Calendar size={10} /> {wi.date}</span>}
                            <span style={{
                              fontSize: '0.7rem', padding: '1px 6px', borderRadius: '4px',
                              backgroundColor: wi.status === 'done' ? 'var(--idx-bg-success)' : wi.status === 'in_progress' ? 'var(--idx-bg-warn)' : 'var(--idx-bg-tertiary)',
                              color: 'var(--idx-text-primary)', border: '1px solid var(--idx-border)',
                            }}>
                              {wi.status === 'done' ? 'Done' : wi.status === 'in_progress' ? 'In Progress' : 'Pending'}
                            </span>
                            {wi.notes && <span style={{ color: 'var(--idx-text-faint)', fontStyle: 'italic', fontSize: '0.8rem' }}>{wi.notes}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Version History */}
                  {agent.versionHistory && agent.versionHistory.length > 0 && (
                    <div className="idx-registry-section">
                      <h5><History size={14} /> Version History</h5>
                      <div className="idx-version-history">
                        {agent.versionHistory.slice().reverse().map((entry, idx) => (
                          <div key={idx} className="idx-version-entry">
                            <span className="idx-version-tag">v{entry.version}</span>
                            <span className="idx-version-date">{entry.date}</span>
                            <span className="idx-version-notes">{entry.notes}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { AgentRegistryTab };
